export type Serializable =
  | string
  | number
  | boolean
  | null
  | Serializable[]
  | { [key: string]: Serializable };

export type BrowserSetupContext<RuntimeProps extends Serializable> = {
  root: HTMLElement;
  scope: string;
  offset: number;
  props: RuntimeProps;
  tl: {
    set: (targets: unknown, vars: Record<string, unknown>, position?: number) => unknown;
    to: (targets: unknown, vars: Record<string, unknown>, position?: number) => unknown;
    fromTo: (
      targets: unknown,
      fromVars: Record<string, unknown>,
      toVars: Record<string, unknown>,
      position?: number,
    ) => unknown;
  };
  gsap: {
    set: (targets: unknown, vars: Record<string, unknown>) => unknown;
  };
};

export type BrowserSetupDefinition<Props extends object> = {
  compile: (props: Props, scope: string, offset: number) => string;
};

export function defineBrowserSetup<Props extends object, RuntimeProps extends Serializable>(
  mapProps: (props: Props) => RuntimeProps,
  run: (context: BrowserSetupContext<RuntimeProps>) => void,
): BrowserSetupDefinition<Props> {
  return {
    compile: (props, scope, offset) =>
      compileBrowserFunction(mapProps(props), run, scope, offset),
  };
}

function serialize(value: Serializable): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

function compileBrowserFunction<RuntimeProps extends Serializable>(
  runtimeProps: RuntimeProps,
  run: (context: BrowserSetupContext<RuntimeProps>) => void,
  scope: string,
  offset: number,
): string {
  const runSource = run.toString();
  return `
    (function(){
      const scope=${serialize(scope)};
      const root=document.querySelector(scope);
      if(!root)throw new Error("Missing HyperFrames component scope: "+scope);
      const __name=(target)=>target;
      (${runSource})({root,scope,offset:${offset},props:${serialize(runtimeProps)},tl,gsap});
    })();
  `;
}

export function compileBrowserSetup<Props extends object>(
  definition: BrowserSetupDefinition<Props>,
  props: Props,
  scope: string,
  offset: number,
): string {
  return definition.compile(props, scope, offset);
}
